import React, { useState, useEffect } from 'react';
import { bookingsAPI, publicAPI, productsAPI } from './services/api';
import PaymentModal from './PaymentModal';
import SalesAgreement from './SalesAgreement';
import { validatePhone, normalizePhone, formatPhoneDisplay } from './utils/phoneUtils';
import './DinnerBooking.css';

interface GuestInfo {
  men: number;
  women: number;
  children: { age: number }[];
}

interface PricingConfig {
  sunbed: { price: number; total: number; rows: number; perRow: number };
  breakfast: { adultPrice: number; child4_6Price: number; child7_12Price: number };
  dinner: { adultPrice: number; child4_6Price: number; child7_12Price: number };
}

const DinnerBooking: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  // Admin panelden kaydedilen kapalı günler (häftanın gün sayıları: 0=Paz, 1=Pzt ...)
  const [closedDays, setClosedDays] = useState<number[]>([1]); // Varsayılan: Pazartesi kapalı
  const [dinnerWebEnabled, setDinnerWebEnabled] = useState<boolean>(true);
  const [blockedDates, setBlockedDates] = useState<{ date: string; reason: string; affects: string[] }[]>([]);
  const [closedDayError, setClosedDayError] = useState<string>('');
  const [guestInfo, setGuestInfo] = useState<GuestInfo>({
    men: 1,
    women: 0,
    children: []
  });
  const [menuHTML, setMenuHTML] = useState<string>('');
  const [pricing, setPricing] = useState<PricingConfig['dinner']>({
    adultPrice: 0,
    child4_6Price: 0,
    child7_12Price: 0
  });
  const [pricingLoaded, setPricingLoaded] = useState(false);
  
  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  
  // Payment Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [createdBookingId, setCreatedBookingId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  
  // Capacity state
  const [capacityInfo, setCapacityInfo] = useState<{
    maxCapacity: number;
    currentReserved: number;
    remainingCapacity: number;
    isFull: boolean;
  } | null>(null);

  useEffect(() => {
    // Backend'den akşam yemeği fiyat/menü ve satış ayarlarını çek
    Promise.all([
      productsAPI.getByType('dinner'),
      productsAPI.getPublicSetting('dinner_public').catch(() => null)
    ]).then(([dinnerRes, dinnerPublicRes]) => {
      if (dinnerRes.data?.data?.content) {
        const content = dinnerRes.data.data.content;
        if (content.price_rules) {
          setPricing({
            adultPrice: content.price_rules.adult ?? 0,
            child4_6Price: content.price_rules.child_0_6 ?? 0,
            child7_12Price: content.price_rules.child_7_12 ?? 0
          });
        }
        if (content.menu_html) {
          setMenuHTML(content.menu_html);
        }
      }

      const dinnerPublicValue = dinnerPublicRes?.data?.data?.value;
      if (dinnerPublicValue) {
        const normalizedDays: number[] = Array.isArray(dinnerPublicValue.closed_days)
          ? Array.from(new Set<number>(dinnerPublicValue.closed_days
              .map((d: any) => Number(d))
              .filter((d: number) => Number.isInteger(d) && d >= 0 && d <= 6)))
              .sort((a, b) => a - b)
          : [1];
        setDinnerWebEnabled(dinnerPublicValue.enabled !== false);
        setClosedDays(normalizedDays.length > 0 ? normalizedDays : [1]);
      }

      setPricingLoaded(true);
    }).catch(e => {
      console.error('Akşam yemeği fiyatları yüklenemedi:', e);
      setPricingLoaded(true);
    });
  }, []);

  // Seçilen tarih değiştiğinde kapasiteyi kontrol et
  useEffect(() => {
    if (selectedDate) {
      fetchCapacity(selectedDate);
    } else {
      setCapacityInfo(null);
    }
  }, [selectedDate]);

  const fetchCapacity = async (date: string) => {
    try {
      const response = await fetch(`/api/products/dinner/capacity/${date}`);
      const data = await response.json();
      if (data.success) {
        setCapacityInfo(data.data);
      }
    } catch (error) {
      console.error('Kapasite bilgisi alınamadı:', error);
    }
  };

  const getChildPrice = (age: number): number => {
    if (age <= 3) return 0;
    if (age <= 6) return pricing.child4_6Price;
    if (age <= 12) return pricing.child7_12Price;
    return pricing.adultPrice;
  };

  const calculateTotal = (): number => {
    const adultTotal = (guestInfo.men + guestInfo.women) * pricing.adultPrice;
    const childrenTotal = guestInfo.children.reduce((sum, child) => sum + getChildPrice(child.age), 0);
    return adultTotal + childrenTotal;
  };

  const getTotalPersons = (): number => {
    return guestInfo.men + guestInfo.women + guestInfo.children.length;
  };

  const handleGuestChange = (type: 'men' | 'women', value: number) => {
    setGuestInfo(prev => ({
      ...prev,
      [type]: Math.max(0, value)
    }));
  };

  const addChild = () => {
    setGuestInfo(prev => ({
      ...prev,
      children: [...prev.children, { age: 1 }]
    }));
  };

  const removeChild = (index: number) => {
    setGuestInfo(prev => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index)
    }));
  };

  const updateChildAge = (index: number, age: number) => {
    setGuestInfo(prev => ({
      ...prev,
      children: prev.children.map((child, i) => i === index ? { age } : child)
    }));
  };

  const handleBooking = async () => {
    if (!dinnerWebEnabled) {
      alert('Akşam yemeği online satışı şu anda kapalıdır.');
      return;
    }

    if (!selectedDate) {
      alert('Lütfen bir tarih seçin!');
      return;
    }

    if (getTotalPersons() === 0) {
      alert('Lütfen en az bir kişi seçin!');
      return;
    }

    if (!customerName.trim()) {
      alert('Lütfen adınızı ve soyadınızı girin!');
      return;
    }

    if (!customerEmail.trim()) {
      alert('Lütfen e-posta adresinizi girin!');
      return;
    }

    const phoneValidation = validatePhone(customerPhone);
    if (!phoneValidation.valid) {
      alert(`Telefon numarası hatalı: ${phoneValidation.error}`);
      return;
    }

    setLoading(true);

    try {
      const bookingData = {
        product_type: 'dinner',
        booking_date: selectedDate,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: phoneValidation.formatted,
        customer_info: {
          men: guestInfo.men,
          women: guestInfo.women,
          children: guestInfo.children
        },
        items: {
          men: guestInfo.men,
          women: guestInfo.women,
          children: guestInfo.children,
          totalPersons: getTotalPersons(),
          pricing: pricing
        },
        total_price: calculateTotal(),
        notes: ''
      };

      const response = await bookingsAPI.create(bookingData);

      if (response.data.success) {
        const { iframeUrl, reservationId } = response.data.data;
        setPaymentUrl(iframeUrl);
        setCreatedBookingId(reservationId);
        setShowPaymentModal(true);
      }
    } catch (err: any) {
      console.error('Rezervasyon hatası:', err);
      alert('Rezervasyon oluşturulamadı: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    publicAPI.getBlockedDates()
      .then(res => { if (res.data.success) setBlockedDates(res.data.data); })
      .catch(() => {});
  }, []);

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const isClosedDay = (dateStr: string): boolean => {
    if (!dateStr) return false;
    const day = new Date(dateStr + 'T12:00:00').getDay();
    return closedDays.includes(day);
  };

  const isDateBlocked = (dateStr: string): boolean => {
    return blockedDates.some(entry =>
      entry.date === dateStr &&
      (entry.affects.includes('all') || entry.affects.includes('dinner'))
    );
  };

  const getBlockedReason = (dateStr: string): string => {
    const entry = blockedDates.find(e =>
      e.date === dateStr &&
      (e.affects.includes('all') || e.affects.includes('dinner'))
    );
    return entry?.reason || '';
  };

  const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

  return (
    <div className="dinner-booking">
      <div className="dinner-container">
        {/* Sol Panel - Menü */}
        <div className="info-panel">
          <div className="info-card">
            <h2>🕐 Servis Saatleri</h2>
            <p><strong>Her Gün</strong></p>
            <p>19:00 - 24:00</p>
          </div>

          <div className="info-card">
            <h2>💰 Fiyat Listesi</h2>
            <div className="price-list">
              <div className="price-item">
                <span>Yetişkin (13+ yaş)</span>
                <span className="price">₺{pricing.adultPrice}</span>
              </div>
              <div className="price-item">
                <span>Çocuk (7-12 yaş)</span>
                <span className="price">₺{pricing.child7_12Price}</span>
              </div>
              <div className="price-item">
                <span>Çocuk (4-6 yaş)</span>
                <span className="price">₺{pricing.child4_6Price}</span>
              </div>
              <div className="price-item">
                <span>Çocuk (0-3 yaş)</span>
                <span className="price free">Ücretsiz</span>
              </div>
            </div>
          </div>

          <div className="info-card menu-card">
            <h2>📋 Akşam Yemeği Menüsü</h2>
            {menuHTML ? (
              <div 
                className="menu-preview-content" 
                dangerouslySetInnerHTML={{ __html: menuHTML }}
              />
            ) : (
              <div className="default-menu">
                <div className="menu-section">
                  <h3>🥗 Başlangıçlar</h3>
                  <ul>
                    <li>Mevsim Salata</li>
                    <li>Közlenmiş Patlıcan Salatası</li>
                    <li>Humus</li>
                    <li>Çoban Salata</li>
                  </ul>
                </div>
                <div className="menu-section">
                  <h3>🍲 Ana Yemekler</h3>
                  <ul>
                    <li>Izgara Somon</li>
                    <li>Dana Bonfile</li>
                    <li>Tavuk Şiş</li>
                    <li>Mantarlı Risotto (Vejetaryen)</li>
                  </ul>
                </div>
                <div className="menu-section">
                  <h3>🍝 Yan Yemekler</h3>
                  <ul>
                    <li>Patates Püresi</li>
                    <li>Közlenmiş Sebzeler</li>
                    <li>Pilav</li>
                    <li>Makarna</li>
                  </ul>
                </div>
                <div className="menu-section">
                  <h3>🍰 Tatlılar</h3>
                  <ul>
                    <li>Tiramisu</li>
                    <li>Sütlaç</li>
                    <li>Mevsim Meyveleri</li>
                    <li>Dondurma</li>
                  </ul>
                </div>
                <div className="menu-section">
                  <h3>🥤 İçecekler</h3>
                  <ul>
                    <li>Sınırsız Çay & Kahve</li>
                    <li>Meyve Suyu</li>
                    <li>Su</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sağ Panel - Rezervasyon Formu */}
        <div className="booking-panel">
          <div className="booking-card">
            <h2>Rezervasyon Yap</h2>

            {!dinnerWebEnabled && (
              <div style={{ marginBottom: '1rem', padding: '0.8rem 1rem', background: '#fff3cd', border: '1px solid #ffecb5', borderRadius: '8px', color: '#664d03' }}>
                Akşam yemeği online satışı şu anda kapalıdır.
              </div>
            )}
            
            {/* Tarih Seçimi */}
            <div className="form-group">
              <label>📅 Tarih Seçin</label>
              <input
                type="date"
                min={getTodayDate()}
                value={selectedDate}
                disabled={!dinnerWebEnabled}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val && isClosedDay(val)) {
                    const day = new Date(val + 'T12:00:00').getDay();
                    setClosedDayError(`${dayNames[day]} günleri tesisimizde akşam yemeği hizmeti verilmemektedir. Lütfen başka bir tarih seçin.`);
                    setSelectedDate('');
                  } else if (val && isDateBlocked(val)) {
                    const reason = getBlockedReason(val);
                    setClosedDayError(`Bu tarihte akşam yemeği servisi verilememektedir${reason ? ` (${reason})` : ''}.`);
                    setSelectedDate('');
                  } else {
                    setClosedDayError('');
                    setSelectedDate(val);
                  }
                }}
                className="date-select"
              />
              {closedDayError && (
                <div style={{ marginTop: '0.5rem', padding: '0.6rem 0.75rem', background: '#fce4ec', border: '1px solid #e91e63', borderRadius: '6px', fontSize: '0.85rem', color: '#880e4f' }}>
                  ⚠️ {closedDayError}
                </div>
              )}
            </div>

            {/* Kapasite Uyarısı */}
            {capacityInfo && selectedDate && (
              <div className={`capacity-info ${capacityInfo.isFull ? 'full' : capacityInfo.remainingCapacity < 10 ? 'warning' : 'available'}`}>
                {capacityInfo.isFull ? (
                  <span>❌ Bu tarih için kapasite dolmuştur</span>
                ) : (
                  <span>
                    ✅ Kalan kapasite: {capacityInfo.remainingCapacity} kişi 
                    ({capacityInfo.currentReserved}/{capacityInfo.maxCapacity})
                  </span>
                )}
              </div>
            )}

            {/* Kişi Sayısı Seçimi */}
            <div className="form-group">
              <label>👥 Yetişkin Sayısı</label>
              
              <div className="person-counter">
                <div className="counter-item">
                  <div className="counter-label">
                    <span className="label-title">Erkek</span>
                    <span className="label-subtitle">₺{pricing.adultPrice}</span>
                  </div>
                  <div className="counter-controls">
                    <button 
                      onClick={() => handleGuestChange('men', guestInfo.men - 1)}
                      disabled={guestInfo.men === 0}
                    >
                      -
                    </button>
                    <span className="counter-value">{guestInfo.men}</span>
                    <button onClick={() => handleGuestChange('men', guestInfo.men + 1)}>
                      +
                    </button>
                  </div>
                </div>

                <div className="counter-item">
                  <div className="counter-label">
                    <span className="label-title">Kadın</span>
                    <span className="label-subtitle">₺{pricing.adultPrice}</span>
                  </div>
                  <div className="counter-controls">
                    <button 
                      onClick={() => handleGuestChange('women', guestInfo.women - 1)}
                      disabled={guestInfo.women === 0}
                    >
                      -
                    </button>
                    <span className="counter-value">{guestInfo.women}</span>
                    <button onClick={() => handleGuestChange('women', guestInfo.women + 1)}>
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Çocuk Bilgileri */}
            <div className="form-group">
              <label>👶 Çocuklar</label>
              
              {guestInfo.children.map((child, index) => (
                <div key={index} className="child-item">
                  <div className="child-info">
                    <span className="child-number">{index + 1}. Çocuk</span>
                    <select 
                      value={child.age}
                      onChange={(e) => updateChildAge(index, parseInt(e.target.value))}
                      className="age-select"
                    >
                      {Array.from({ length: 13 }, (_, i) => (
                        <option key={i} value={i}>{i} yaş</option>
                      ))}
                    </select>
                    <span className="child-price">₺{getChildPrice(child.age).toLocaleString()}</span>
                  </div>
                  <button 
                    className="btn-remove-child"
                    onClick={() => removeChild(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              
              <button className="btn-add-child" onClick={addChild}>
                + Çocuk Ekle
              </button>
            </div>

            {/* Özet */}
            <div className="booking-summary-box">
              <div className="summary-row">
                <span>Toplam Kişi:</span>
                <span className="summary-value">{getTotalPersons()} kişi</span>
              </div>
              <div className="summary-row">
                <span>Yetişkin:</span>
                <span className="summary-value">{guestInfo.men + guestInfo.women} kişi</span>
              </div>
              <div className="summary-row">
                <span>Çocuk:</span>
                <span className="summary-value">{guestInfo.children.length} kişi</span>
              </div>
              <div className="summary-row total-row">
                <span>Toplam Ücret:</span>
                <span className="summary-price">₺{calculateTotal().toLocaleString()}</span>
              </div>
            </div>

            {/* Müşteri Bilgileri */}
            {getTotalPersons() > 0 && selectedDate && (
              <div className="customer-info-form" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#2c3e50' }}>👤 İletişim Bilgileri</h3>
                
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Ad Soyad *</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Adınız ve Soyadınız"
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                    required
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>E-posta Adresi *</label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="ornek@email.com"
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                    required
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Telefon Numarası *</label>
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value.replace(/[^0-9+\s]/g, ''))}
                    onBlur={() => { const v = validatePhone(customerPhone); if (v.valid) setCustomerPhone(formatPhoneDisplay(customerPhone)); }}
                    placeholder="05XX XXX XX XX"
                    maxLength={20}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                    required
                  />
                  {customerPhone && !validatePhone(customerPhone).valid && (
                    <small style={{ color: '#e74c3c', marginTop: '4px', display: 'block' }}>⚠️ {validatePhone(customerPhone).error}</small>
                  )}
                </div>
              </div>
            )}

            {/* Satış Sözleşmesi */}
            <div style={{ marginTop: '1.5rem' }}>
              <SalesAgreement
                isChecked={agreementAccepted}
                onCheckChange={setAgreementAccepted}
              />
            </div>

            <button 
              className="btn-dinner-book" 
              onClick={handleBooking}
              disabled={loading || !dinnerWebEnabled || !selectedDate || getTotalPersons() === 0 || !customerName.trim() || !customerEmail.trim() || !customerPhone.trim() || !agreementAccepted}
            >
              {loading ? '⏳ İşleniyor...' : '💳 Ödeme Sayfasına Geç'}
            </button>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal 
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        iframeUrl={paymentUrl}
        bookingId={createdBookingId}
        skipAgreement={true}
      />
    </div>
  );
};

export default DinnerBooking;
